import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Detect LLM budget exhaustion and upgrade prompts for gentle banners
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail || "";
    if (err?.response?.status === 502 && /budget/i.test(detail)) {
      window.dispatchEvent(new CustomEvent("llm-budget-exceeded"));
    }
    if (err?.response?.status === 402) {
      window.dispatchEvent(new CustomEvent("needs-upgrade", { detail }));
    }
    return Promise.reject(err);
  }
);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Start an async generation job and poll until the lecture is ready.
// Avoids the ~60s gateway timeout for long generations. Paste-text + Certification depth
// can legitimately take up to ~8 minutes so we cap the poll at 10.
export const generateLecture = async (payload, onTick = null) => {
  const { job_id } = await api.post("/generate/start", payload).then((r) => r.data);
  const deadline = Date.now() + 10 * 60 * 1000; // 10 min safety cap (was 4 min)
  const startedAt = Date.now();
  while (Date.now() < deadline) {
    await sleep(3000);
    const { status, lecture, detail, progress } = await api
      .get(`/generate/status/${job_id}`)
      .then((r) => r.data);
    if (onTick) {
      try { onTick({ status, progress, elapsed_ms: Date.now() - startedAt, job_id }); } catch {}
    }
    if (status === "done") return lecture;
    if (status === "error") throw new Error(detail || "Generation failed. Please try again.");
  }
  // If we timed out client-side, tell the user their lecture is likely landing in the library.
  const err = new Error(
    "This one's taking longer than usual. Keep the tab open — it'll finish in your Library shortly. If it doesn't, check My Library in a minute."
  );
  err.job_id = job_id;
  throw err;
};
export const listLectures = () => api.get("/lectures").then((r) => r.data);
export const listCommunityLectures = () => api.get("/lectures/community").then((r) => r.data);
export const listSavedLectures = () => api.get("/lectures/saved").then((r) => r.data);
export const toggleLike = (id) => api.post(`/lectures/${id}/like`).then((r) => r.data);
export const toggleSave = (id) => api.post(`/lectures/${id}/save`).then((r) => r.data);
export const reportInaccuracy = (id, payload) => api.post(`/lectures/${id}/report`, payload).then((r) => r.data);
export const getUploadLimits = () => api.get("/me/upload-limits").then((r) => r.data);
export const getAvAnalytics = (days = 7) => api.get(`/admin/av-analytics?days=${days}`).then((r) => r.data);
export const getAvStatus = () => api.get("/av/status").then((r) => r.data);

// YouTube → text
export const ingestYouTube = (url, preferLanguage = "en") =>
  api.post("/upload/youtube", { url, prefer_language: preferLanguage }).then((r) => r.data);
export const searchYouTube = (q, limit = 8) =>
  api.get("/upload/youtube/search", { params: { q, limit } }).then((r) => r.data);

// Forgot / reset password
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, new_password) =>
  api.post("/auth/reset-password", { token, new_password }).then((r) => r.data);

// PIN lock
export const pinStatus = (device_id = "") =>
  api.get("/auth/pin/status", { params: device_id ? { device_id } : {} }).then((r) => r.data);
export const setPin = (pin, current_password = "") => api.post("/auth/pin/set", { pin, current_password }).then((r) => r.data);
export const removePin = (current_password) => api.post("/auth/pin/remove", { pin: "", current_password }).then((r) => r.data);
export const verifyPin = (pin, opts = {}) =>
  api.post("/auth/pin/verify", {
    pin,
    device_id: opts.device_id || "",
    trust_device: !!opts.trust_device,
    device_label: opts.device_label || "",
  }).then((r) => r.data);
export const listTrustedDevices = () => api.get("/auth/pin/trusted-devices").then((r) => r.data);
export const revokeTrustedDevice = (device_id) =>
  api.post("/auth/pin/trusted-devices/revoke", { device_id }).then((r) => r.data);

// Login history
export const getLoginHistory = (limit = 10) =>
  api.get("/auth/login-history", { params: { limit } }).then((r) => r.data);

// Passkeys / biometrics
export const passkeyRegisterStart = () => api.post("/auth/passkeys/register/start").then((r) => r.data);
export const passkeyRegisterFinish = (payload) => api.post("/auth/passkeys/register/finish", payload).then((r) => r.data);
export const listPasskeys = () => api.get("/auth/passkeys").then((r) => r.data);
export const deletePasskey = (id) => api.delete(`/auth/passkeys/${id}`).then((r) => r.data);
export const passkeyLoginStart = (email) => api.post("/auth/passkeys/login/start", { email }).then((r) => r.data);
export const passkeyLoginFinish = (payload) => api.post("/auth/passkeys/login/finish", payload).then((r) => r.data);

// Payments (Stripe one-time Pro passes)
export const getPaymentsConfig = () => api.get("/payments/config").then((r) => r.data);

// Google Sign-In (Emergent-managed) — exchanges session_id for our JWT cookie
export const googleCallback = (session_id, ref = "") => api.post("/auth/google/callback", { session_id, ref }).then((r) => r.data);

// Referrals
export const getReferralInfo = () => api.get("/referral/me").then((r) => r.data);
export const getReferralLeaderboard = () => api.get("/referral/leaderboard").then((r) => r.data);

// Preferences (weekly-digest opt-in)
export const getPreferences = () => api.get("/me/preferences").then((r) => r.data);
export const updatePreferences = (payload) => api.patch("/me/preferences", payload).then((r) => r.data);
export const previewWeeklyDigest = () => api.get("/me/weekly-digest/preview").then((r) => r.data);

// Admin weekly-digest preview (spot-check recipients before batch send)
export const adminWeeklyRecipients = (limit = 50) =>
  api.get("/admin/weekly-digest/recipients", { params: { limit } }).then((r) => r.data);
export const adminWeeklyPreview = (email) =>
  api.get("/admin/weekly-digest/preview", { params: { email } }).then((r) => r.data);

// Generation config (depth presets)
export const getGenerateConfig = () => api.get("/generate/config").then((r) => r.data);

// Admin sales dashboard
export const getAdminSales = () => api.get("/admin/sales").then((r) => r.data);
export const getAdminRecentSignups = (days = 7, limit = 50) =>
  api.get("/admin/recent-signups", { params: { days, limit } }).then((r) => r.data);

// Review nudges
export const getGoogleReviewNudge = () => api.get("/nudges/google-review").then((r) => r.data);
export const ackGoogleReviewNudge = (action) => api.post("/nudges/google-review/ack", { action }).then((r) => r.data);

// Classroom — instructor
export const becomeInstructor = () => api.post("/me/become-instructor").then((r) => r.data);
export const createClassroom = (payload) => api.post("/instructor/classrooms", payload).then((r) => r.data);
export const listClassrooms = () => api.get("/instructor/classrooms").then((r) => r.data);
export const getClassroom = (id) => api.get(`/instructor/classrooms/${id}`).then((r) => r.data);
export const updateClassroom = (id, payload) => api.patch(`/instructor/classrooms/${id}`, payload).then((r) => r.data);
export const removeStudentFromClass = (cid, sid) => api.delete(`/instructor/classrooms/${cid}/students/${sid}`).then((r) => r.data);
export const createAssignment = (cid, payload) => api.post(`/instructor/classrooms/${cid}/assignments`, payload).then((r) => r.data);
export const getAssignmentDetail = (cid, aid) => api.get(`/instructor/classrooms/${cid}/assignments/${aid}`).then((r) => r.data);
export const gradebookCsvUrl = (cid) => `${API}/instructor/classrooms/${cid}/gradebook.csv`;
// Class library — instructor-only staging area (hidden from students until an item is assigned).
export const getClassLibrary = (cid) => api.get(`/instructor/classrooms/${cid}/library`).then((r) => r.data);
export const attachLectureToClass = (cid, lid, chapter_label = "") => api.post(`/instructor/classrooms/${cid}/library/${lid}/attach`, { chapter_label }).then((r) => r.data);
export const detachLectureFromClass = (cid, lid) => api.delete(`/instructor/classrooms/${cid}/library/${lid}`).then((r) => r.data);
export const listUnassignedLectures = () => api.get(`/instructor/library/unassigned`).then((r) => r.data);
// Bulk invites + seat dashboard
export const bulkInvite = (cid, emails) => api.post(`/instructor/classrooms/${cid}/invites/bulk`, { emails }).then((r) => r.data);
export const listInvites = (cid) => api.get(`/instructor/classrooms/${cid}/invites`).then((r) => r.data);
export const revokeInvite = (cid, inviteId) => api.delete(`/instructor/classrooms/${cid}/invites/${inviteId}`).then((r) => r.data);
export const acceptInvite = (token) => api.post(`/student/accept-invite`, { token }).then((r) => r.data);
export const getSeatDashboard = (cid) => api.get(`/instructor/classrooms/${cid}/seat-dashboard`).then((r) => r.data);

// Live Game (Kahoot-killer)
export const startGame = (lecture_id, opts = {}) => api.post(`/games/start`, { lecture_id, ...opts }).then((r) => r.data);
export const joinGame = (code, name = "") => api.post(`/games/join`, { code, name }).then((r) => r.data);
export const getGame = (game_id) => api.get(`/games/${game_id}`).then((r) => r.data);
export const getGameByCode = (code) => api.get(`/games/by-code/${code}`).then((r) => r.data);
export const watchGame = (code) => api.get(`/games/watch/${code}`).then((r) => r.data);
// Jeopardy — host-only pick/judge + player buzz
export const jeopardyPick = (game_id, key) => api.post(`/games/${game_id}/jeopardy/pick`, { key }).then((r) => r.data);
export const jeopardyBuzz = (game_id) => api.post(`/games/${game_id}/jeopardy/buzz`).then((r) => r.data);
export const jeopardyJudge = (game_id, team, correct) => api.post(`/games/${game_id}/jeopardy/judge`, { team, correct }).then((r) => r.data);
export const advanceGame = (game_id) => api.post(`/games/${game_id}/advance`).then((r) => r.data);
export const answerGame = (game_id, choice_index, time_ms, use_power = null) =>
  api.post(`/games/${game_id}/answer`, { choice_index, time_ms, use_power }).then((r) => r.data);
export const endGame = (game_id) => api.post(`/games/${game_id}/end`).then((r) => r.data);
export const reactInGame = (game_id, emoji) => api.post(`/games/${game_id}/react`, { emoji }).then((r) => r.data);
export const startPopQuiz = (classroom_id, lecture_id, slide_indices = []) =>
  api.post(`/games/pop-quiz`, { classroom_id, lecture_id, slide_indices }).then((r) => r.data);
export const getPendingAmbush = () => api.get(`/games/pending-ambush`).then((r) => r.data);
// Avatars
export const listAvatars = () => api.get(`/avatars`).then((r) => r.data);
export const updateMyAvatar = (avatar) => api.patch(`/users/me/avatar`, { avatar }).then((r) => r.data);

// Classroom — student
export const joinClass = (code) => api.post("/student/join-class", { code }).then((r) => r.data);
export const listMyClasses = () => api.get("/student/classrooms").then((r) => r.data);
export const listMyAssignments = (cid) => api.get(`/student/classrooms/${cid}/assignments`).then((r) => r.data);
export const startAssignment = (aid) => api.post(`/student/assignments/${aid}/start`).then((r) => r.data);
export const heartbeatAssignment = (aid, seconds) => api.post(`/student/assignments/${aid}/heartbeat`, { seconds }).then((r) => r.data);
export const completeAssignment = (aid, score) => api.post(`/student/assignments/${aid}/complete`, { score }).then((r) => r.data);

// Bilingual: start async English translation of a non-English creation, then poll.
export const getBilingual = async (id) => {
  const start = await api.post(`/lectures/${id}/bilingual/start`).then((r) => r.data);
  if (start.status === "done" && start.english_version) return start.english_version;
  const jobId = start.job_id;
  const deadline = Date.now() + 4 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 3000));
    const { status, english_version, detail } = await api
      .get(`/lectures/bilingual/status/${jobId}`)
      .then((r) => r.data);
    if (status === "done") return english_version;
    if (status === "error") throw new Error(detail || "Translation failed. Please try again.");
  }
  throw new Error("Translation is taking longer than expected. Please try again.");
};
export const getLecture = (id) => api.get(`/lectures/${id}`).then((r) => r.data);
export const deleteLecture = (id) => api.delete(`/lectures/${id}`).then((r) => r.data);

// Public read-only share links
export const createShareLink = (id) => api.post(`/lectures/${id}/share`).then((r) => r.data);
export const revokeShareLink = (id) => api.delete(`/lectures/${id}/share`).then((r) => r.data);
export const getPublicLecture = (token) => api.get(`/public/lectures/${token}`).then((r) => r.data);
export const listFeaturedGuides = (limit = 24) => api.get(`/public/featured`, { params: { limit } }).then((r) => r.data);

// Kick off an async generation job without polling — used by bulk upload where we
// fire N jobs in parallel and let them complete in the background. Returns {job_id, status}.
export const startGenerationJob = (payload) => api.post(`/generate/start`, payload).then((r) => r.data);

// File upload → text extraction (for the "Upload files" create-form mode).
// Images run OCR in the background so a stack of scanned pages doesn't block
// the request — we poll the status endpoint until "done" | "error".
export const uploadExtract = async (file, onProgress = null) => {
  const isImage = /^image\//i.test(file.type) || /\.(png|jpe?g|webp|heic|heif)$/i.test(file.name || "");
  const form = new FormData();
  form.append("file", file);
  const url = isImage ? "/upload/extract?background=true" : "/upload/extract";
  const initial = await api.post(url, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
    timeout: 5 * 60 * 1000,
  }).then((r) => r.data);

  // Synchronous path (PDF/DOCX/text) — return immediately
  if (initial.text != null) return initial;

  // Background OCR — poll until done
  const jobId = initial.job_id;
  if (!jobId) throw new Error("Upload response missing job_id");
  const deadline = Date.now() + 3 * 60 * 1000; // 3 min OCR safety cap
  while (Date.now() < deadline) {
    await sleep(2000);
    const status = await api.get(`/upload/extract/status/${jobId}`).then((r) => r.data);
    if (status.status === "done") {
      return {
        filename: status.filename,
        kind: status.kind,
        chars: status.chars,
        truncated: status.truncated,
        suggested_title: status.suggested_title,
        text: status.text,
      };
    }
    if (status.status === "error") {
      throw new Error(status.detail || "Could not read this image.");
    }
  }
  throw new Error("Image OCR timed out. Try a smaller or clearer image.");
};

// Drag-and-drop organize (move lectures between My Library / Class → Chapter)
export const organizeLecture = (id, { classroom_id, chapter_label }) =>
  api.patch(`/lectures/${id}/organize`, { classroom_id, chapter_label }).then((r) => r.data);
export const bulkOrganizeLectures = ({ lecture_ids, classroom_id, chapter_label }) =>
  api.post(`/lectures/bulk-organize`, { lecture_ids, classroom_id, chapter_label }).then((r) => r.data);

export const saveScore = (id, score) => api.post(`/lectures/${id}/score`, { score }).then((r) => r.data);
export const getNarration = (id, voice) =>
  api.post(`/lectures/${id}/narration`, null, { params: { voice } }).then((r) => r.data);
export const synthSpeech = (text, voice = "sage") =>
  api.post("/tts", { text, voice }).then((r) => r.data);
export const recordMastery = (id, results) =>
  api.post(`/lectures/${id}/mastery`, { results }).then((r) => r.data);

export const audioUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

export const getDashboard = () => api.get("/me/dashboard").then((r) => r.data);
export const startCheckout = (origin_url, lookup_key = "pro_monthly", opts = {}) => api.post("/payments/checkout", { origin_url, lookup_key, ...opts }).then((r) => r.data);
export const paymentStatus = (sid) => api.get(`/payments/status/${sid}`).then((r) => r.data);
export const createExamPlan = (payload) => api.post("/exam-plans", payload).then((r) => r.data);
export const listExamPlans = () => api.get("/exam-plans").then((r) => r.data);
export const toggleExamTask = (id, index, done) => api.patch(`/exam-plans/${id}/task`, { index, done }).then((r) => r.data);
export const deleteExamPlan = (id) => api.delete(`/exam-plans/${id}`).then((r) => r.data);
export const getQuickStudy = () => api.post("/quick-study").then((r) => r.data);
export const instructorGift = (email) => api.post("/instructor/gift", { email }).then((r) => r.data);
export const listInstructorGifts = () => api.get("/instructor/gifts").then((r) => r.data);
export const instructorRevoke = (email) => api.post("/instructor/revoke", { email }).then((r) => r.data);
export const bulkGift = (emails) => api.post("/instructor/gift-bulk", { emails }).then((r) => r.data);
export const createCodes = (count) => api.post("/instructor/codes", { count }).then((r) => r.data);
export const listCodes = () => api.get("/instructor/codes").then((r) => r.data);
export const getSeats = () => api.get("/instructor/seats").then((r) => r.data);
export const redeemCode = (code) => api.post("/redeem-code", { code }).then((r) => r.data);
export const getSubscription = () => api.get("/subscription").then((r) => r.data);

// --- Growth / marketing capture endpoints ------------------------------------
export const submitTeacherApplication = (payload) =>
  api.post("/growth/teacher-application", payload).then((r) => r.data);
export const submitLaunchNotify = (email) =>
  api.post("/growth/launch-notify", { email }).then((r) => r.data);

// --- Admin: launch-day emailer ----------------------------------------------
export const getLaunchEmailerPreview = () =>
  api.get("/admin/launch-emailer/preview").then((r) => r.data);
export const sendLaunchEmailerBlast = (payload) =>
  api.post("/admin/launch-emailer/send", { confirm: true, ...(payload || {}) }).then((r) => r.data);

// --- Testimonials -----------------------------------------------------------
export const submitTestimonial = (payload) =>
  api.post("/testimonials/submit", payload).then((r) => r.data);
export const listApprovedTestimonials = () =>
  api.get("/testimonials/approved").then((r) => r.data);
export const listPendingTestimonials = () =>
  api.get("/admin/testimonials/pending").then((r) => r.data);
export const approveTestimonial = (id, edits = {}) =>
  api.post(`/admin/testimonials/${id}/approve`, edits).then((r) => r.data);
export const rejectTestimonial = (id) =>
  api.post(`/admin/testimonials/${id}/reject`).then((r) => r.data);

// --- Admin: beta lifetime codes ---------------------------------------------
export const generateBetaCodes = (count = 5, label = "") =>
  api.post("/admin/beta-codes", { count, label }).then((r) => r.data);
export const listBetaCodes = () =>
  api.get("/admin/beta-codes").then((r) => r.data);

// --- Study groups -----------------------------------------------------------
export const createStudyGroup = (name) =>
  api.post("/study-groups/create", { name }).then((r) => r.data);
export const previewStudyGroup = (code) =>
  api.get(`/study-groups/${encodeURIComponent(code)}/preview`).then((r) => r.data);
export const joinStudyGroup = (code) =>
  api.post(`/study-groups/${encodeURIComponent(code)}/join`).then((r) => r.data);
export const listMyStudyGroups = () =>
  api.get("/study-groups/mine").then((r) => r.data);
export const postStudyGroupReaction = (code, payload) =>
  api.post(`/study-groups/${encodeURIComponent(code)}/reactions`, payload).then((r) => r.data);
export const listStudyGroupReactions = (code) =>
  api.get(`/study-groups/${encodeURIComponent(code)}/reactions`).then((r) => r.data);

export { API };
